/**
 * 请求/响应拦截器工厂
 */

import { decryptResponseData, encryptRequestData } from '../crypto/factory';
import { isExcludedPath } from '../crypto/path-matcher';
import {
  DEFAULT_ENCRYPT_HEADER,
  DEFAULT_SIGNATURE_HEADER,
  DEFAULT_TIMESTAMP_HEADER,
} from './constants';
import type { AuthInterceptorConfig, EncryptInterceptorConfig } from './types';

function isBinaryPayload(data: unknown): boolean {
  return (
    (typeof FormData !== 'undefined' && data instanceof FormData) ||
    (typeof Blob !== 'undefined' && data instanceof Blob) ||
    (typeof File !== 'undefined' && data instanceof File)
  );
}

/**
 * 创建认证请求拦截器
 *
 * 自动为请求添加认证 token 和额外的 headers。
 *
 * @param config - 认证配置
 * @returns UmiJS 请求拦截器函数
 */
export function createAuthInterceptor(config: AuthInterceptorConfig) {
  const {
    tokenKey,
    headerName,
    headerPrefix = '',
    excludePaths = [],
    extraHeaders = {},
  } = config;

  return (url: string, options: Record<string, unknown>) => {
    const headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
    };

    // 添加 token（try-catch 防止 SSR/隐私模式下 localStorage 不可用）
    let token: string | null = null;
    try {
      token = localStorage.getItem(tokenKey);
    } catch {
      // localStorage 不可用（SSR、隐私模式等），跳过 token 注入
    }
    if (token) {
      const isExcluded =
        excludePaths.length > 0 && isExcludedPath(url, excludePaths);

      if (!isExcluded) {
        headers[headerName] = `${headerPrefix}${token}`;
      }
    }

    // 添加额外请求头
    for (const [key, value] of Object.entries(extraHeaders)) {
      if (value !== undefined && value !== null) {
        headers[key] = String(value);
      }
    }

    return {
      url,
      options: { ...options, headers },
    };
  };
}

/**
 * 创建加密请求拦截器
 *
 * 自动加密请求体，添加加密标识、时间戳和 HMAC 签名请求头。
 *
 * @param config - 加密配置
 * @returns UmiJS 请求拦截器函数（异步）
 */
export function createEncryptRequestInterceptor(
  config: EncryptInterceptorConfig,
) {
  const {
    enabled,
    strategy,
    excludePaths = [],
    methods = ['POST', 'PUT'],
    overridableMethods = ['GET', 'DELETE'],
    encryptHeader = DEFAULT_ENCRYPT_HEADER,
    timestampHeader = DEFAULT_TIMESTAMP_HEADER,
    signatureHeader = DEFAULT_SIGNATURE_HEADER,
  } = config;

  const METHOD_OVERRIDE_HEADER = 'X-HTTP-Method-Override';

  return async (url: string, options: Record<string, unknown>) => {
    if (!enabled) return { url, options };

    const nextOptions: Record<string, unknown> = { ...options };
    const headers: Record<string, string> = {
      ...((nextOptions.headers as Record<string, string>) || {}),
    };

    // 检查路径排除
    if (excludePaths.length > 0 && isExcludedPath(url, excludePaths)) {
      return { url, options };
    }

    // 跳过文件上传类型
    if (isBinaryPayload(nextOptions.data)) {
      return { url, options: { ...nextOptions, headers } };
    }

    let method = ((nextOptions.method as string) || '').toUpperCase();

    // 可覆盖方法（如 GET/DELETE）携带 params 时，转为 POST+data 以便加密
    if (
      overridableMethods.includes(method) &&
      nextOptions.params &&
      typeof nextOptions.params === 'object' &&
      Object.keys(nextOptions.params as Record<string, unknown>).length > 0
    ) {
      headers[METHOD_OVERRIDE_HEADER] = method;
      nextOptions.method = 'POST';
      nextOptions.data = nextOptions.params;
      nextOptions.params = undefined;
      method = 'POST';
    }

    const shouldEncryptBody = methods.includes(method);

    if (shouldEncryptBody) {
      // 无 body 时补充空对象，确保后端 ApiEncryptFilter 能正确解密
      const payload = nextOptions.data || {};
      const { encrypted, timestamp, signature } = await encryptRequestData(
        strategy,
        payload,
      );

      nextOptions.data = config.wrapEncryptedBody
        ? config.wrapEncryptedBody(encrypted)
        : encrypted;
      headers[encryptHeader] = 'true';
      headers[timestampHeader] = String(timestamp);
      if (signature) headers[signatureHeader] = signature;
      headers['Content-Type'] = 'text/plain;charset=UTF-8';
    }

    return { url, options: { ...nextOptions, headers } };
  };
}

/**
 * 创建解密响应拦截器
 *
 * 加密启用时 responseType:'text'，所有响应均为字符串：
 * - X-Encrypted: true → 解密
 * - 其他 → JSON.parse 还原
 */
export function createDecryptResponseInterceptor(
  config: EncryptInterceptorConfig,
) {
  const {
    enabled,
    strategy,
    excludePaths = [],
    encryptHeader = DEFAULT_ENCRYPT_HEADER,
    timestampHeader = DEFAULT_TIMESTAMP_HEADER,
    signatureHeader = DEFAULT_SIGNATURE_HEADER,
  } = config;

  return async (response: Record<string, unknown>) => {
    const getHeader = (name: string): string | null => {
      const headers = response.headers as Record<string, string> | undefined;
      if (!headers) return null;
      return headers[name.toLowerCase()] ?? headers[name] ?? null;
    };

    const isResponseEncrypted = getHeader(encryptHeader) === 'true';

    if (!enabled) {
      if (isResponseEncrypted) {
        const reqUrl =
          ((response.config as Record<string, unknown>)?.url as string) || '';
        console.error(
          `[加密配置] 服务端返回了加密响应（${encryptHeader}: true），但前端加密未启用。` +
            `请检查加密配置是否正确。URL: ${reqUrl}`,
        );
      }
      return response;
    }

    const reqUrl =
      ((response.config as Record<string, unknown>)?.url as string) || '';

    if (
      isResponseEncrypted &&
      !(excludePaths.length > 0 && isExcludedPath(reqUrl, excludePaths))
    ) {
      // 加密响应：解密 Base64 字符串（responseType:'text' 确保 data 为原始字符串）
      const encryptedString = config.unwrapEncryptedResponse
        ? config.unwrapEncryptedResponse(response.data)
        : String(response.data || '');

      if (encryptedString) {
        const timestamp = Number.parseInt(
          getHeader(timestampHeader) || '0',
          10,
        );
        const signature = getHeader(signatureHeader) || undefined;

        response.data = await decryptResponseData(
          strategy,
          encryptedString,
          timestamp || undefined,
          { signature },
        );
      }
    } else if (typeof response.data === 'string') {
      // 非加密响应（排除路径等）：responseType:'text' 导致为字符串，手动解析为 JSON
      try {
        response.data = JSON.parse(response.data);
      } catch {
        // 非 JSON 响应，保持原样
      }
    }

    return response;
  };
}
